/**
 * Shared Career Studio Primitives (definition-v2 — re-derived domain-def@4)
 *
 * The cross-cutting spine + value objects shared across every Career Studio
 * aggregate. Injected into every domain's ArkType scope during compilation (see
 * `compiler.ts`).
 *
 * Re-derivation principle (domain-model.md §1.1): **one shared scope kills the
 * enum-mirror tax.** Every type referenced by ≥2 aggregates lives HERE and is
 * merged into each definition's compiled scope — no literal-union mirrors, no
 * "keep in lock-step" comments. (Fixes recon-03 critique 1.)
 *
 * The compiler merges `{ ...coreDomain, ...careerPrimitives, ...def.types }`
 * into ONE isolated scope per aggregate. A consequence: a shared type that is a
 * typed UNION over other named shapes (e.g. `ContextSnapshot`) can only resolve
 * if those member shapes are ALSO in scope everywhere — so the three produced
 * context documents (`CandidateContext` / `SearchContext` / `ProfilerRubric`),
 * pinned by Application as reproducibility snapshots, are authored HERE and
 * merely *referenced* by the Workshop that produces them (domain-model.md §2,
 * DOM-m4). See the DEVIATIONS note in the handoff.
 *
 * All values are ArkType string expressions or plain object definitions —
 * JSON-serializable, no functions, no morphs. Closed objects use `"+": "reject"`.
 */

export const careerPrimitives = {
  // ─── Scalars ────────────────────────────────────────────────

  /** Non-empty string — used for all identifiers and required prose */
  NonEmptyString: "string > 0",

  /** ISO 8601 timestamp */
  Timestamp: "string > 0",

  /** Career URN — `urn:career:<type>:<id>` (see index.ts CAREER_URN_PREFIX) */
  Urn: "string > 0",

  /** Owner identity — the Keycloak user (sub UUID or username). Per-user scope key. */
  UserId: "string > 0",

  /** BCP-47 locale, candidate-facing output language, e.g. "de-DE" (i18n, per-user) */
  Locale: "string > 0",

  /** Confidence / strength in [0,1] */
  UnitInterval: "0 <= number <= 1",

  /** Freely-authored user-facing prose (markdown). A distinct alias so authored
   *  prose reads differently from bookkeeping strings at every call site. */
  Markdown: "string > 0",

  // ─── Object store ────────────────────────────────────────────

  /**
   * ObjectRef — reference to a binary/large artifact in the S3 object store
   * (platform Garage/S3, per-user prefixed). Events carry the object key, not
   * the bytes. Used by Document, Asset, Cv render, ApplicationPackage,
   * portraits, VoiceProfile samples.
   */
  ObjectRef: {
    "+": "reject",
    /** Per-user-prefixed object key (e.g. "career/<userId>/documents/<id>.pdf") */
    key: "NonEmptyString",
    "bucket?": "string",
    "contentType?": "string",
    "sizeBytes?": "number >= 0",
    /** Content hash for integrity / dedupe */
    "sha256?": "string",
  },

  // ─── Evidence Ledger spine ───────────────────────────────────

  /** Evidence grade — the load-bearing field that makes "never fabricate" enforceable */
  EvidenceGrade: "'evidenced' | 'self-positioning' | 'gap'",

  /** How a claim entered the ledger (domain-model.md §3.1: ingested | elicited | manual) */
  EvidenceOrigin: "'ingested' | 'elicited' | 'manual'",

  /** Confirmation lifecycle — nothing is treated as evidenced until confirmed (law C5) */
  ClaimStatus: "'proposed' | 'confirmed'",

  /** Evidence tag facet (skill / value / motivation / domain) */
  EvidenceTag: {
    "+": "reject",
    facet: "'skill' | 'value' | 'motivation' | 'domain'",
    label: "NonEmptyString",
  },

  // EvidenceRef — a discriminated union pointing at the addressable source
  // material a claim cites. Each variant carries a `kind` discriminator and an
  // optional verbatim `quote`.

  /** Source: an addressable span inside an uploaded Document's parsed text */
  DocumentSpanRef: {
    "+": "reject",
    kind: "'document_span'",
    documentId: "Urn",
    "section?": "string",
    "startOffset?": "number >= 0",
    "endOffset?": "number >= 0",
    "quote?": "string",
  },

  /** Source: a specific entry inside the durable Cv */
  CvEntryRef: {
    "+": "reject",
    kind: "'cv_entry'",
    cvId: "Urn",
    "section?": "string",
    "entryId?": "NonEmptyString",
    "quote?": "string",
  },

  /** Source: a typed Asset — certificate, reference, diploma, … */
  AssetRef: {
    "+": "reject",
    kind: "'asset'",
    assetId: "Urn",
    "quote?": "string",
  },

  /** Source: a prior Application the user already authored */
  PriorApplicationRef: {
    "+": "reject",
    kind: "'prior_application'",
    applicationId: "Urn",
    "quote?": "string",
  },

  /** Source: an external link (portfolio, public profile, …) */
  ExternalLinkRef: {
    "+": "reject",
    kind: "'external_link'",
    url: "NonEmptyString",
    "quote?": "string",
  },

  /** Evidence reference — the discriminated union of all source kinds */
  EvidenceRef:
    "DocumentSpanRef | CvEntryRef | AssetRef | PriorApplicationRef | ExternalLinkRef",

  /**
   * EvidenceClaim — the ledger entity, the spine of the whole domain. The
   * Evidence stream (`…::Evidence`) is the ONLY home of claim bodies
   * (domain-model.md §3.0 rule 1); every other aggregate references a claim by
   * URN, never by copy.
   *
   * Invariants (the type carries the fields; full conditional enforcement is a
   * validate-time check):
   * - `grade === "evidenced"` ⇒ `sources` MUST be non-empty and resolvable.
   * - `grade === "gap"` ⇒ never citable as proof; `sources` is typically empty.
   * - `status === "proposed"` ⇒ never treated as evidenced until `confirmed`.
   */
  EvidenceClaim: {
    "+": "reject",
    id: "Urn",
    owner: "UserId",
    statement: "NonEmptyString",
    grade: "EvidenceGrade",
    origin: "EvidenceOrigin",
    status: "ClaimStatus",
    /** Required for `evidenced` (validate-time check); may be empty for `gap` */
    sources: "EvidenceRef[]",
    tags: "EvidenceTag[]",
    "confidence?": "UnitInterval",
    "notes?": "string",
  },

  // ─── Provenance (PROV-O aligned) ─────────────────────────────

  /**
   * ProvenanceActor — who performed an activity. `engine` belongs on the Run
   * step, NOT here.
   */
  ProvenanceActor: {
    "+": "reject",
    actorId: "NonEmptyString",
    kind: "'human' | 'ai'",
    /** Model name for AI actors; absent for humans */
    "model?": {
      "+": "reject",
      name: "string",
    },
  },

  /** ProvenanceActivity — what was done (a single Run step) */
  ProvenanceActivity: {
    "+": "reject",
    runId: "NonEmptyString",
    activityType: "NonEmptyString",
    startedAt: "Timestamp",
    "endedAt?": "Timestamp",
    associatedWith: "ProvenanceActor[]",
  },

  /** ProvenanceEntity — what was produced */
  ProvenanceEntity: {
    "+": "reject",
    entityUrn: "Urn",
    entityType: "NonEmptyString",
  },

  /** Full provenance bundle (PROV-O aligned) — threaded onto generated content */
  ProvenanceBundle: {
    "+": "reject",
    entity: "ProvenanceEntity",
    activity: "ProvenanceActivity",
    evidence: "EvidenceRef[]",
  },

  // ─── Voice ───────────────────────────────────────────────────

  /**
   * VoiceProfile — the user's own writing samples for voice preservation.
   * Referenced by Ingest + Profile → cross-cutting.
   */
  VoiceProfile: {
    "+": "reject",
    "locale?": "Locale",
    /** Extracted writing samples (the user's own words) */
    samples: "string[]",
    /** Optional consolidated style notes */
    "styleNotes?": "string",
    "objectRef?": "ObjectRef | null",
  },

  // ─── Alignment / context references ──────────────────────────

  /**
   * Alignment dimension — the authentic axis first. Used by the W3
   * ProfilerRubric (criteria) and by Match (signals) → cross-cutting.
   */
  AlignmentDimension: "'fit' | 'social' | 'motivational' | 'goal'",

  /** The kinds of typed context document produced/derived across the pipeline */
  ContextKind:
    "'candidate' | 'search' | 'opportunity' | 'application' | 'profiler_rubric'",

  /** A URN reference to a typed context document (by kind), pinned with a version */
  ContextRef: {
    "+": "reject",
    kind: "ContextKind",
    urn: "Urn",
    "version?": "number.integer >= 1",
  },

  // ─── Match / search tier (one vocabulary, domain-model.md §2) ─

  /**
   * MatchTier — the ONE tier vocabulary, used by the Workshop rubric quality
   * floor AND the Opportunity match verdict. Replaces the old `SearchTier` ≡
   * `MatchTier` mirror pair (recon-03 critique 1). NOTE the re-derived values
   * (`strong | viable | weak | unsuited`) differ from the quarry's
   * `strong | moderate | weak`.
   */
  MatchTier: "'strong' | 'viable' | 'weak' | 'unsuited'",

  // ─── CV section vocabulary (shared by Cv + Application) ───────

  /**
   * CvSectionName — the JSON-Resume section vocabulary. ONE name set, shared by
   * `Cv` (per-entry evidence links, per-section metadata) and `Application`
   * (CvTailoring sections, edit targets). Replaces the old
   * `CvSectionName`/`TailoringSectionName` mirror. This is the full 12-section
   * superset (basics/interests included for tailoring visibility); entry-level
   * evidence links only ever reference the entry-bearing sections in practice.
   */
  CvSectionName:
    "'basics' | 'work' | 'volunteer' | 'education' | 'awards' | 'certificates' | 'publications' | 'skills' | 'languages' | 'interests' | 'references' | 'projects'",

  // ─── LLM engine (matches @platform/llm AiEngine + RunStep.engine) ─────────────

  /**
   * Provider engine captured on a RunStep. `noop` is the career deterministic backend;
   * `synthetic` is the `@platform/llm` seam's name for the same no-network default (its
   * `runStepFromResponse`/`RunStepFrom` default + the pipeline's synthetic steps write it).
   * Both are accepted so the RAW captured value validates (capture raw, project later, law C9).
   * Never the WIP gateway (domain-model.md §8).
   */
  RunEngine: "'claude' | 'codex' | 'openrouter' | 'noop' | 'synthetic'",

  /** Address register — derived at verify/org-research time, threaded into every
   *  presentation pass; never a prompt constant (domain-model.md §2, LLM-M3). */
  AddressRegister: "'du' | 'Sie'",

  // ─── Presentation + provenance side-channel (domain-model.md §2, llm-workflows §3) ─

  /**
   * PresentationMeta — every LLM-authored prose field carries where/how it was
   * authored. `promptRef` = "<seam>@<n>" (the exact prompt version); `groundingRef`
   * = content hash of the loaded grounding bundle; `runRef` = the Run URN whose
   * step authored the text. Stamped in code, never by the model.
   */
  PresentationMeta: {
    "+": "reject",
    locale: "Locale",
    /** "<seam>@<n>" — the exact prompt-artifact version that authored the text */
    promptRef: "NonEmptyString",
    /** Content hash of the runtime-loaded grounding bundle (grounding-drift attribution) */
    groundingRef: "NonEmptyString",
    /** The Run URN whose RunStep authored this text */
    runRef: "Urn",
    /**
     * True when the presented text is the DETERMINISTIC composer fallback, not a real
     * model authoring (the transport failed or a structural provenance defect forced
     * degradation). ADDITIVE + optional: absent ⇒ authored. The event/fallback SHAPE
     * stays identical (llm-workflows §3a) — this marker + a UI honesty cue mean a
     * degraded narrative never silently presents as authored prose.
     */
    "degraded?": "boolean",
    /** Why it degraded (transport error / schema-invalid reply / structural provenance defect). */
    "degradeReason?": "string",
  },

  /** A VERBATIM substring of a presented body — the only anchor code can verify
   *  (uniqueness enforced by occurrence rules at validation time). */
  PassageAnchor: "NonEmptyString",

  /** One citation-map entry: an anchor into the presented body + the raw-bundle
   *  URNs it draws on (`refs` ⊆ the raw bundle). */
  ProvenanceEntry: {
    "+": "reject",
    anchor: "PassageAnchor",
    refs: "Urn[]",
    /** Occurrence index when the anchor appears more than once in the body */
    "occurrence?": "number.integer >= 0",
  },

  /** The citation side-channel of a presented text (llm-workflows §3). */
  ProvenanceMap: "ProvenanceEntry[]",

  /**
   * PresentedText — the output shape of a PRESENTATION pass: freely-authored
   * prose + its citation side-channel + authorship provenance. Shared by every
   * simple presented-narrative event (workshop context narrative, org summary).
   * Richer presented artifacts (cover letter, opportunity brief) add their own
   * fields alongside `provenance` + `meta`.
   */
  PresentedText: {
    "+": "reject",
    body: "Markdown",
    provenance: "ProvenanceMap",
    meta: "PresentationMeta",
  },

  // ─── Reviewable proposed edits (unified, shared by Application) ─

  /** Lifecycle of a proposed edit — a string-literal union, never a TS enum. */
  EditStatus: "'proposed' | 'approved' | 'rejected'",

  /** Which durable content span a proposed edit targets. */
  EditTargetKind: "'cover_letter_paragraph' | 'cv_tailoring_section'",

  /**
   * EditTarget — the addressed span. `paragraphIndex` indexes the deterministically
   * segmented cover-letter paragraphs (llm-workflows §3a); `section` names the
   * CvTailoring section for a `cv_tailoring_section` edit.
   */
  EditTarget: {
    "+": "reject",
    kind: "EditTargetKind",
    "paragraphIndex?": "number.integer >= 0",
    "section?": "CvSectionName",
  },

  /**
   * TextAnchor — a text-quote anchor into the rendered target so the UI can
   * attach a change AT the document rather than only listing it. Optional —
   * a whole-target rewrite with no locatable span degrades to the rail list.
   */
  TextAnchor: {
    "+": "reject",
    /** The exact selected text — the primary anchor. */
    exact: "NonEmptyString",
    "prefix?": "string",
    "suffix?": "string",
    "start?": "number.integer >= 0",
    "end?": "number.integer >= 0",
  },

  /**
   * ProposedEdit — a staged, reviewable change to durable content. Authored at
   * `status: 'proposed'` and sits there until the owner approves; only then does
   * `after` land (the human gate). `before` is the target's content captured
   * server-side at propose time (the conflict-guard baseline); `origin` names the
   * proposer (`'user' | 'claude' | …`, a free string). The richer application
   * shape wins the old two-divergent-`ProposedEdit` conflict (domain-model.md §2).
   */
  ProposedEdit: {
    "+": "reject",
    id: "Urn",
    applicationRef: "Urn",
    target: "EditTarget",
    before: "string",
    after: "NonEmptyString",
    status: "EditStatus",
    origin: "NonEmptyString",
    "rationale?": "string",
    "anchor?": "TextAnchor",
    createdAt: "Timestamp",
    "resolvedAt?": "Timestamp",
  },

  // ─── Werte-Sektion (W1 redesign, §6) — value pyramid + science anchoring ─────
  //
  // The personal-values instrument (Workshop 1 »Werte-Kartenspiel« → qualitative
  // interview → statements pendant). Authored HERE (the shared spine) so
  // `CandidateContext` can reference `PersonalValueEntry` / `PersonalStatements`
  // and every aggregate scope resolves them — the same reason the produced context
  // documents live here (see the header note). V1 is types + catalog ONLY; the
  // events/seams/UI land in later slices (redesign §12). The Schwartz + Big-Five
  // card annotation is STATIC catalog data (`value-card-catalog.ts`), not events —
  // these are the shapes that data and the downstream analysis speak in.

  /** Pyramid tiers — 1 Lebenswert · 3 Superkräfte · 4 Basiswerte (the 1·3·4 shape, §9). */
  ValuePyramidTier: "'Lebenswert' | 'Superkräfte' | 'Basiswerte'",

  /** One placed card in the pyramid — the stable `cardId` is the comparability key (§9). */
  ValuePyramidSlot: {
    "+": "reject",
    cardId: "NonEmptyString",
    name: "NonEmptyString",
    tier: "ValuePyramidTier",
  },

  /** The Phase-1 artifact: the 8 chosen cards with their tiers (1·3·4). */
  ValuePyramid: {
    "+": "reject",
    slots: "ValuePyramidSlot[]",
    formedAt: "Timestamp",
  },

  // ─ Science-anchoring layer (§5a): Schwartz (values) + Big Five (working-style) ─

  /** Two axes from the start (Q4) — Schwartz is the values axis, Big Five the working-style axis. */
  ValueFramework: "'schwartz' | 'big_five'",

  /**
   * A single model-anchoring tag. For `schwartz`: `valueType` is the basic value
   * (e.g. "achievement") and `higherOrder` the higher-order dimension (e.g.
   * "self_enhancement"). For `big_five`: `valueType` is the OCEAN trait (e.g.
   * "conscientiousness") and `higherOrder` the pole ("high" | "low"). The
   * `framework` discriminator is what keeps the two axes separable — a single
   * `ValueModelReadout` NEVER mixes frameworks (Q4).
   */
  ValueModelTag: {
    "+": "reject",
    framework: "ValueFramework",
    valueType: "NonEmptyString",
    higherOrder: "NonEmptyString",
  },

  /**
   * Which WAY an answer went, per id the card put on the table — the sign the fold's
   * `importance` alone cannot express (`@platform/elicitation-core`'s `AnswerDirection`,
   * expressed as career's persistence). `id` is a card option / catalog `cardId`;
   * `weight` is signed in [-1, 1]: positive = moved toward it, negative = away. Without
   * this, endorsing and rejecting the same stimulus fold identically and every probed
   * value reads as support.
   */
  AnswerDirection: {
    "+": "reject",
    id: "NonEmptyString",
    weight: "-1 <= number <= 1",
  },

  /** Per-dimension coverage + confidence (the score math is composed in code, §5a). */
  DimensionCoverage: {
    "+": "reject",
    framework: "ValueFramework",
    dimension: "NonEmptyString",
    coverage: "UnitInterval",
    confidence: "UnitInterval",
  },

  /** A single-framework readout — one readout per axis, NEVER mixed (Q4). */
  ValueModelReadout: {
    "+": "reject",
    framework: "ValueFramework",
    dimensions: "DimensionCoverage[]",
    note: "string",
  },

  // ─ Per-value detail (§6) — definition/leitsatz for TOP-4 only (Q5) ─

  /** A surfaced lived episode (STAR/CAR) — opportunistic, never quota-driven (§5b). */
  IconicStory: {
    "+": "reject",
    title: "NonEmptyString",
    situation: "string",
    challenge: "string",
    action: "string",
    result: "string",
    learning: "string",
  },

  /**
   * One entry in the personal Werte-Sektion. `cardId` is the stable catalog id
   * (the comparability key, §9). `definition`/`leitsatz` are OPTIONAL precisely so
   * the 4 Basiswerte ship selected-with-tags (Q5) and can GAIN definitions later
   * without a schema break; examples/story are opportunistic (captured when the
   * interview surfaces a lived episode, never required).
   */
  PersonalValueEntry: {
    "+": "reject",
    cardId: "NonEmptyString",
    tier: "ValuePyramidTier",
    name: "NonEmptyString",
    "tags?": "ValueModelTag[]",
    "definition?": "string",
    "leitsatz?": "string",
    "positiveExamples?": "string[]",
    "negativeExamples?": "string[]",
    "story?": "IconicStory | null",
    "claimRefs?": "Urn[]",
  },

  // ─ Culminating artifact: the statements pendant (§5b) — all four always attempted ─

  /** One personal statement — the individual pendant to a company statement section. */
  PersonalStatement: {
    "+": "reject",
    kind: "'warum' | 'positionierung' | 'philosophie' | 'vision'",
    text: "NonEmptyString",
    "claimRefs?": "Urn[]",
  },

  /** The four statements + one readout per framework (Schwartz + Big Five, never merged). */
  PersonalStatements: {
    "+": "reject",
    statements: "PersonalStatement[]",
    "readouts?": "ValueModelReadout[]",
  },

  // ─── Produced context documents (authored here so ContextSnapshot is typed) ─
  //
  // These three are the outputs a Workshop produces and an Application pins as a
  // reproducibility snapshot. They live in the shared scope so the typed
  // `ContextSnapshot` union resolves in EVERY aggregate (see the header note).

  /** A consolidated, evidence-graded theme inside a context document */
  ContextTheme: {
    "+": "reject",
    label: "NonEmptyString",
    detail: "string",
    "claimRefs?": "Urn[]",
  },

  /** W1 output — consolidated self-knowledge for generation. Raw part only;
   *  the German narrative is authored in the presentation pass.
   *  `values`/`statements` are the ADDITIVE Werte-Sektion (redesign §6): old
   *  candidate contexts without them fold via read-path repair (`values → []`,
   *  `statements → null`), so the ContextSnapshot union + reproducibility pin stay
   *  valid (union-schema on every read path). */
  CandidateContext: {
    "+": "reject",
    id: "Urn",
    owner: "UserId",
    kind: "'candidate'",
    themes: "ContextTheme[]",
    strengths: "ContextTheme[]",
    gaps: "ContextTheme[]",
    locale: "Locale",
    prov: "ProvenanceBundle",
    createdAt: "Timestamp",
    "values?": "PersonalValueEntry[]",
    "statements?": "PersonalStatements | null",
  },

  /**
   * SearchConfig — the STRUCTURED, USER-AUTHORABLE targeting spec discovery
   * searches against. The explicit lists are AUTHORITATIVE: W2 elicitation drafts
   * them; the user's edits win. Empty `[]` is allowed; each list is present so the
   * editor always has a field to fill.
   */
  SearchConfig: {
    "+": "reject",
    roles: ["string[]", "@", "Ziel-Rollenfamilien bzw. Stellentitel, nach denen gesucht wird"],
    queries: ["string[]", "@", "Konkrete Suchbegriffe für Jobbörsen/SERP"],
    seniority: ["string[]", "@", "Senioritätsstufen, z. B. Senior, Lead, Principal"],
    locations: ["string[]", "@", "Zielorte bzw. Remote"],
    excludeKeywords: ["string[]", "@", "Harte Ausschlüsse, z. B. Praktikum, Werkstudent"],
    summary: ["string", "@", "Freitext-Suchintention (steuert Query-Aufbau + Bewertung; darf leer sein)"],
    "sourceAllowlist?": "string[]",
    "allowCompanyWebsites?": "boolean",
    /** Quality floor — discard matches below this tier (one MatchTier vocabulary). */
    "minTier?": "MatchTier | null",
    "limit?": "number.integer > 0",
  },

  /** W2 output — what to look for and where. Raw part only. */
  SearchContext: {
    "+": "reject",
    id: "Urn",
    owner: "UserId",
    kind: "'search'",
    config: "SearchConfig",
    rationale: "string",
    "claimRefs?": "Urn[]",
    prov: "ProvenanceBundle",
    createdAt: "Timestamp",
  },

  /** A single weighted criterion in the ideal-opportunity rubric */
  RubricCriterion: {
    "+": "reject",
    dimension: "AlignmentDimension",
    label: "NonEmptyString",
    weight: "UnitInterval",
    description: "string",
  },

  /** W3 output — the ideal-opportunity rubric that drives matching. */
  ProfilerRubric: {
    "+": "reject",
    id: "Urn",
    owner: "UserId",
    kind: "'profiler_rubric'",
    criteria: "RubricCriterion[]",
    antiValues: "string[]",
    "locationConstraint?": "string | null",
    prov: "ProvenanceBundle",
    createdAt: "Timestamp",
  },

  /**
   * ContextSnapshot — an immutable TYPED capture of a produced context, pinned
   * for reproducibility (domain-model.md §2). The typed union (over the three
   * produced context documents) replaces the old `payload: unknown` escape hatch
   * — the shared scope makes the reproducibility boundary typed (fixes critique 4).
   */
  ContextSnapshot: "CandidateContext | SearchContext | ProfilerRubric",
} as const
